import "@/App.css";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { Suspense, lazy } from "react";
import { Loader2 } from "lucide-react";
import HealthGate from "@/components/HealthGate";

// ─── Inline loading spinner for lazy chunks ───
function PageLoader() {
  return (
    <div className="min-h-screen bg-background flex items-center justify-center">
      <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
    </div>
  );
}

// ─── GUEST FLOW (small, fast bundle — loaded immediately for clients) ───
import AgentGuestPortalPage from "@/pages/AgentGuestPortalPage";
import WelcomePage from "@/pages/WelcomePage";
import GatePage from "@/pages/GatePage";
import ServicesPage from "@/pages/ServicesPage";
import SummaryPage from "@/pages/SummaryPage";
import ConfirmationPage from "@/pages/ConfirmationPage";
import ResumeBookingPage from "@/pages/ResumeBookingPage";
import PortalHomePage from "@/pages/PortalHomePage";

// ─── LAZY: Yacht & Experience catalogue (loaded on demand) ───
const YachtCategoriesPage = lazy(() => import("@/pages/YachtCategoriesPage"));
const YachtListingPage = lazy(() => import("@/pages/YachtListingPage"));
const YachtDetailPage = lazy(() => import("@/pages/YachtDetailPage"));
const ExperienceCategoriesPage = lazy(() => import("@/pages/ExperienceCategoriesPage"));
const ExperienceListingPage = lazy(() => import("@/pages/ExperienceListingPage"));
const ExperienceDetailPage = lazy(() => import("@/pages/ExperienceDetailPage"));

// ─── LAZY: Card capture & preferences ───
const CardCapturePage = lazy(() => import("@/pages/CardCapturePage"));
const StandaloneCardCapturePage = lazy(() => import("@/pages/StandaloneCardCapturePage"));
const PreferencesPage = lazy(() => import("@/pages/PreferencesPage"));
const MagicLinkPage = lazy(() => import("@/pages/MagicLinkPage"));

// ─── LAZY: Utility pages ───
const TermsPage = lazy(() => import("@/pages/TermsPage"));
const PrivacyPage = lazy(() => import("@/pages/PrivacyPage"));
const ResetPasswordPage = lazy(() => import("@/pages/ResetPasswordPage"));
const ApproveQuotePage = lazy(() => import("@/pages/ApproveQuotePage"));
const ClientBookingViewPage = lazy(() => import("@/pages/ClientBookingViewPage"));

// ─── LAZY: Agent portal (only agents need this) ───
const AgentLoginPage = lazy(() => import("@/pages/AgentLoginPage"));
const AgentDashboardPage = lazy(() => import("@/pages/AgentDashboardPage"));
const AgentBookingDetailPage = lazy(() => import("@/pages/AgentBookingDetailPage"));

// ─── LAZY: Admin panel (only admins need this) ───
const AdminLoginPage = lazy(() => import("@/pages/admin/AdminLoginPage"));
const AdminDashboardPage = lazy(() => import("@/pages/admin/AdminDashboardPage"));
const AdminBookingDetailPage = lazy(() => import("@/pages/admin/AdminBookingDetailPage"));
const SuperAdminLoginPage = lazy(() => import("@/pages/admin/SuperAdminLoginPage"));
const SuperAdminDashboardPage = lazy(() => import("@/pages/admin/SuperAdminDashboardPage"));
const AdminServicesPage = lazy(() => import("@/pages/admin/AdminServicesPage"));
const AdminYachtsPage = lazy(() => import("@/pages/admin/AdminYachtsPage"));
const AdminExperiencesPage = lazy(() => import("@/pages/admin/AdminExperiencesPage"));
const AdminPrivateChefsPage = lazy(() => import("@/pages/admin/AdminPrivateChefsPage"));
const AdminDestinationsPage = lazy(() => import("@/pages/admin/AdminDestinationsPage"));
const AdminCategoriesPage = lazy(() => import("@/pages/admin/AdminCategoriesPage"));
const AdminSuppliersPage = lazy(() => import("@/pages/admin/AdminSuppliersPage"));
const AdminFinancialsPage = lazy(() => import("@/pages/admin/AdminFinancialsPage"));
const AdminSettingsPage = lazy(() => import("@/pages/admin/AdminSettingsPage"));
const AdminEmailLogsPage = lazy(() => import("@/pages/admin/AdminEmailLogsPage"));
const AdminOrphanedImagesPage = lazy(() => import("@/pages/admin/AdminOrphanedImagesPage"));
const ServiceCardPrototype = lazy(() => import("@/pages/admin/prototype/ServiceCardPrototype"));
const CommandCentrePage = lazy(() => import("@/pages/admin/CommandCentrePage"));
const HubDetailPage = lazy(() => import("@/pages/admin/HubDetailPage"));
const EntryDetailPage = lazy(() => import("@/pages/admin/EntryDetailPage"));
const SupplierConfirmPage = lazy(() => import("@/pages/SupplierConfirmPage"));
const OperationsCentrePage = lazy(() => import("@/pages/admin/OperationsCentrePage"));
const ConciergeServicesPage = lazy(() => import("@/pages/admin/ConciergeServicesPage"));
const CRMPage = lazy(() => import("@/pages/admin/CRMPage"));
const ClientsListPage = lazy(() => import("@/pages/admin/ClientsListPage"));
const ClientProfilePage = lazy(() => import("@/pages/admin/ClientProfilePage"));
const GuestFormPage = lazy(() => import("@/pages/GuestFormPage"));

function App() {
  return (
    <div className="min-h-screen">
      <Toaster position="top-center" richColors />
      <HealthGate>
      <BrowserRouter>
        <Suspense fallback={<PageLoader />}>
        <Routes>
          {/* Global Portal Entry */}
          <Route path="/" element={<PortalHomePage />} />
          <Route path="/login" element={<AgentLoginPage />} />
          <Route path="/reset-password/:token" element={<ResetPasswordPage />} />
          <Route path="/approve-quote/:quoteId" element={<ApproveQuotePage />} />
          <Route path="/agent" element={<AgentDashboardPage />} />
          <Route path="/agent/bookings/:id" element={<AgentBookingDetailPage />} />
          
          {/* White-Label Agent Guest Portals */}
          <Route path="/a/:agentSlug" element={<AgentGuestPortalPage />} />
          
          {/* Resume Booking (for guests returning via magic link) */}
          <Route path="/resume/:bookingId" element={<ResumeBookingPage />} />
          
          {/* Legacy Charles Marlow Direct Routes (redirect to /a/charles-marlow) */}
          <Route path="/welcome" element={<WelcomePage />} />
          <Route path="/gate" element={<GatePage />} />
          
          {/* Guest Flow (shared across all agents) */}
          <Route path="/services" element={<ServicesPage />} />
          <Route path="/summary" element={<SummaryPage />} />
          <Route path="/preferences" element={<PreferencesPage />} />
          <Route path="/card-capture" element={<CardCapturePage />} />
          <Route path="/card-capture/success" element={<StandaloneCardCapturePage />} />
          <Route path="/card-capture/:token" element={<StandaloneCardCapturePage />} />
          <Route path="/confirmation" element={<ConfirmationPage />} />
          <Route path="/guest/:token" element={<MagicLinkPage />} />
          <Route path="/my-booking/:token" element={<ClientBookingViewPage />} />
          
          {/* Yacht Catalogue */}
          <Route path="/yachts" element={<YachtCategoriesPage />} />
          <Route path="/yachts/:category" element={<YachtListingPage />} />
          <Route path="/yacht/:yachtId" element={<YachtDetailPage />} />
          
          {/* Experiences Catalogue */}
          <Route path="/experiences" element={<ExperienceCategoriesPage />} />
          <Route path="/experiences/:category" element={<ExperienceListingPage />} />
          <Route path="/experience/:experienceId" element={<ExperienceDetailPage />} />
          <Route path="/terms" element={<TermsPage />} />
          <Route path="/privacy" element={<PrivacyPage />} />
          <Route path="/supplier-confirm/:token" element={<SupplierConfirmPage />} />
          <Route path="/forms/:token" element={<GuestFormPage />} />
          
          {/* Super Admin CMS */}
          <Route path="/admin" element={<SuperAdminLoginPage />} />
          <Route path="/admin/dashboard" element={<SuperAdminDashboardPage />} />
          <Route path="/admin/services" element={<AdminServicesPage />} />
          <Route path="/admin/yachts" element={<AdminYachtsPage />} />
          <Route path="/admin/experiences" element={<AdminExperiencesPage />} />
          <Route path="/admin/private-chefs" element={<AdminPrivateChefsPage />} />
          <Route path="/admin/categories" element={<AdminCategoriesPage />} />
          <Route path="/admin/destinations" element={<AdminDestinationsPage />} />
          <Route path="/admin/suppliers" element={<AdminSuppliersPage />} />
          <Route path="/admin/financials" element={<AdminFinancialsPage />} />
          <Route path="/admin/settings" element={<AdminSettingsPage />} />
          <Route path="/admin/email-logs" element={<AdminEmailLogsPage />} />
          <Route path="/admin/orphaned-images" element={<AdminOrphanedImagesPage />} />
          <Route path="/admin/command-centre" element={<CommandCentrePage />} />
          <Route path="/admin/command-centre/hub/:hubId" element={<HubDetailPage />} />
          <Route path="/admin/command-centre/entry/:entryId" element={<EntryDetailPage />} />
          
          {/* Legacy Admin Routes (kept for compatibility) */}
          <Route path="/admin/legacy" element={<AdminLoginPage />} />
          <Route path="/admin/legacy/dashboard" element={<AdminDashboardPage />} />
          <Route path="/admin/bookings/:id" element={<AdminBookingDetailPage />} />
          <Route path="/admin/operations" element={<OperationsCentrePage />} />
          <Route path="/admin/prototype/service-card" element={<ServiceCardPrototype />} />
          <Route path="/prototype/service-card" element={<ServiceCardPrototype />} />
          <Route path="/admin/concierge-services" element={<ConciergeServicesPage />} />
          <Route path="/admin/crm" element={<CRMPage />} />
          <Route path="/admin/crm/clients" element={<ClientsListPage />} />
          <Route path="/admin/crm/clients/:id" element={<ClientProfilePage />} />
        </Routes>
        </Suspense>
      </BrowserRouter>
      </HealthGate>
    </div>
  );
}

export default App;
